import { absoluteTimeRangeLabel, isAbsoluteTimeRange, parseAbsoluteTimeRange } from './absoluteTimeRange';

/**
 * The time ranges a dashboard can be viewed over, shortest first. Wire tokens the backend parses
 * (`app/dashboards/time_range.py`): `m` is a month, minutes are spelled out. Nothing the reader
 * sees relies on that — `timeRangeLabel` writes them out in full.
 */
export const TIME_RANGE_PRESETS = ['15min', '1h', '6h', '1d', '1w', '1m', '3m', '6m', 'all'] as const;

/** Whole history: sorts after every bounded range. */
const ALL_TIME_RANGE = 'all';

const UNIT_TO_MINUTES: Record<string, number> = { min: 1, h: 60, d: 1440, w: 10080, m: 43200 };

/** Minutes a range covers, for ordering. `all` — and anything unparseable — sorts last. */
export const timeRangeMinutes = (value: string): number => {
  if (value.trim().toLowerCase() === ALL_TIME_RANGE) return Number.POSITIVE_INFINITY;
  const match = /^\s*(\d+)\s*(min|[mhdw])\s*$/.exec(value);
  return match ? Number(match[1]) * UNIT_TO_MINUTES[match[2]] : Number.POSITIVE_INFINITY;
};

/** Insert a range where its duration belongs, so the picker stays chronological. */
const insertByDuration = (ranges: string[], value: string): string[] => {
  if (ranges.includes(value)) return ranges;
  const minutes = timeRangeMinutes(value);
  const index = ranges.findIndex((range) => timeRangeMinutes(range) >= minutes);
  return index === -1 ? [...ranges, value] : [...ranges.slice(0, index), value, ...ranges.slice(index)];
};

/**
 * The ranges to offer: the presets, plus any range that must remain selectable even though
 * it isn't one — a template default from before this list, or the range currently applied
 * (a shared URL, or one written by an older build). Each is inserted where its duration
 * belongs, never appended, and never twice. An absolute range is not a quick one: its own
 * fields show it.
 */
export const timeRangeOptions = (...extras: (string | undefined)[]): string[] =>
  extras
    .filter((value): value is string => !!value && !isAbsoluteTimeRange(value))
    .reduce<string[]>(insertByDuration, [...TIME_RANGE_PRESETS]);

/** Singular/plural unit names, keyed by the wire token's unit letter(s). */
const UNIT_NAMES: Record<string, [singular: string, plural: string]> = {
  min: ['minute', 'minutes'],
  h: ['hour', 'hours'],
  d: ['day', 'days'],
  w: ['week', 'weeks'],
  m: ['month', 'months'],
};

/**
 * `15min` → "Last 15 minutes", `1m` → "Last month", `all` → "All time", an absolute range → its
 * bounds. An amount of one is left implicit; an unparseable token falls back to itself rather
 * than being mislabelled.
 */
export const timeRangeLabel = (value: string): string => {
  const token = value.trim();
  if (token.toLowerCase() === ALL_TIME_RANGE) return 'All time';

  const absolute = parseAbsoluteTimeRange(token);
  if (absolute) return absoluteTimeRangeLabel(absolute);

  const match = /^\s*(\d+)\s*(min|[mhdw])\s*$/.exec(token);
  if (!match) return token;

  const amount = Number(match[1]);
  const names = UNIT_NAMES[match[2]];
  if (!names) return token;

  const [singular, plural] = names;
  return amount === 1 ? `Last ${singular}` : `Last ${amount} ${plural}`;
};
